import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchApi } from '@/hooks/useApi'
import {
  SUPPORTED_LANGS,
  clearPendingLanguageChoice,
  hasPendingLanguageChoice,
  resolveLang,
  type Lang,
} from '@/i18n'
import type { UserProfile } from '@/types/api'

function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

function storedUser(): UserProfile | null {
  try {
    const stored = localStorage.getItem('user')
    return stored ? (JSON.parse(stored) as UserProfile) : null
  } catch {
    return null
  }
}

function profileLanguage(profile: UserProfile): Lang | undefined {
  return SUPPORTED_LANGS.find((lang) => lang === profile.language)
}

/**
 * Tiene allineati profilo e browser su lingua e fuso (#346): il backend li usa
 * per l'intestazione delle email citate nei follow-up.
 *
 * La lingua del profilo segue l'utente: un browser dove non l'ha scelta (nuovo,
 * in incognito, con lo storage svuotato) parte dalla lingua di ripiego e adotta
 * quella del profilo, senza sovrascriverla. Verso il server la lingua va solo
 * quando l'utente l'ha scelta su questo browser, o quando il profilo non ne ha
 * una. Il fuso è sempre quello del browser.
 *
 * Parte all'ingresso nell'app (quindi dopo il login) e a ogni cambio lingua.
 * Se il salvataggio fallisce non si vede nulla: il backend continua con i
 * valori che ha.
 */
export function useSyncUserLocale(userId: string | undefined) {
  const { i18n } = useTranslation()
  const language = i18n.language ? resolveLang() : undefined

  useEffect(() => {
    if (!userId || !language) return
    const profile = storedUser()
    if (!profile) return
    const saved = profileLanguage(profile)

    if (saved && saved !== language && !hasPendingLanguageChoice()) {
      // L'effetto riparte con la lingua del profilo
      void i18n.changeLanguage(saved)
      return
    }
    if (saved === language) clearPendingLanguageChoice()

    const timezone = browserTimeZone()
    if (saved === language && (!timezone || profile.timezone === timezone)) return

    fetchApi<UserProfile>('/api/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ language, ...(timezone ? { timezone } : {}) }),
    })
      .then((updated) => {
        // La scelta è arrivata sul profilo, salvo un cambio successivo
        if (updated.language === language && resolveLang() === language) {
          clearPendingLanguageChoice()
        }
        const current = storedUser()
        // Nel frattempo l'utente può essere uscito o cambiato
        if (!current || current.id !== updated.id) return
        localStorage.setItem(
          'user',
          JSON.stringify({ ...current, language: updated.language, timezone: updated.timezone }),
        )
      })
      .catch(() => {
        // Best effort: si riprova al prossimo ingresso o cambio lingua
      })
  }, [userId, language, i18n])
}
